// @ts-nocheck
import React, { useRef, useState } from "react";
import { Button, Input, Skeleton, useToast } from "@/shared/components/ui";
import { EmailVerify } from "./EmailVerify";
import { PasswordField } from "./PasswordField";
import {
  filterPersonNameInput,
  validateAuthEmail,
  validatePersonName,
  validatePasswordStrength,
} from "@/legacy/helpersModule";

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (pw) => pw.length >= 8 },
  { label: "One uppercase letter (A–Z)", test: (pw) => /[A-Z]/.test(pw) },
  { label: "One lowercase letter (a–z)", test: (pw) => /[a-z]/.test(pw) },
  { label: "One special character (!@#$%^&*)", test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

export function CandReg({ onRegister, onBack, onGoToLogin, initialName = "", initialEmail = "" }) {
  const [f, setF] = useState({ name: initialName || "", email: initialEmail || "", password: "", phone: "" });
  const [emailVerified, setEmailVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const toast = useToast();
  const accountExistsToastForRef = useRef("");
  const phoneValid = /^[6-9]\d{9}$/.test(f.phone.trim());
  const nameValid = validatePersonName(f.name).ok;
  const emailValid = validateAuthEmail(f.email).ok;
  const passwordCheck = validatePasswordStrength(f.password);
  const passwordValid = passwordCheck.ok;
  const showPasswordChecklist = f.password.length > 0;
  const canSubmit = Boolean(
    nameValid && emailValid && passwordValid && phoneValid && emailVerified,
  );

  const showAccountExistsToast = (email) => {
    if (!email || typeof onGoToLogin !== "function") return;
    if (accountExistsToastForRef.current === email) return;
    accountExistsToastForRef.current = email;
    toast.show({
      tone: "warning",
      title: "Account already exists",
      message: "You already have an account with this email.",
      action: {
        label: "Log in →",
        onClick: () => onGoToLogin(email),
      },
      durationMs: 0,
    });
  };

  const updateEmail = (v) => {
    setF((p) => ({ ...p, email: v }));
    if (emailVerified) setEmailVerified(false);
    accountExistsToastForRef.current = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setNameTouched(true);
    setEmailTouched(true);
    setPhoneTouched(true);
    if (!canSubmit || submitting) return;
    const nameErr = validatePersonName(f.name);
    if (!nameErr.ok) {
      toast.show({ tone: "warning", message: nameErr.message });
      return;
    }
    const emailErr = validateAuthEmail(f.email);
    if (!emailErr.ok) {
      toast.show({ tone: "warning", message: emailErr.message });
      return;
    }
    const pwErr = validatePasswordStrength(f.password);
    if (!pwErr.ok) {
      toast.show({ tone: "warning", message: pwErr.message });
      return;
    }
    setSubmitting(true);
    try {
      // Resolves after register + state sync + navigation; on success this
      // component unmounts, on failure we fall through and restore the form.
      await onRegister(f);
    } catch (err) {
      if (err?.code === "account_exists") {
        showAccountExistsToast(err.email || f.email);
        return;
      }
      toast.show({
        tone: "danger",
        title: "Registration failed",
        message: (err && err.message) || "Could not create your account. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-dvh safe-bottom bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="text-ink-400 hover:text-ink-900 mb-6 text-sm disabled:opacity-40"
        >
          ← Back
        </button>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-8 shadow-sm">
          <h1 className="text-lg sm:text-2xl font-black text-ink-900 mb-6">
            {submitting ? "Signing you in…" : "Create Account"}
          </h1>

          {submitting ? (
            <div className="space-y-5" aria-busy="true" aria-live="polite">
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 border-2 border-brand-300 border-t-brand-600 rounded-full animate-spin" />
                <span className="text-ink-600 text-sm font-semibold">
                  Creating your account and logging you in…
                </span>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-11 w-full rounded-control" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-11 w-full rounded-control" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-11 w-full rounded-control" />
              </div>
              <Skeleton className="h-11 w-full rounded-control" />
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-2">Full Name</label>
              <Input
                type="text"
                value={f.name}
                onChange={(e) => setF((p) => ({ ...p, name: filterPersonNameInput(e.target.value) }))}
                onBlur={() => setNameTouched(true)}
              />
              {nameTouched && f.name.trim() && !nameValid ? (
                <p className="text-xs text-brand-bad mt-1">
                  {validatePersonName(f.name).message}
                </p>
              ) : null}
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-2">Email</label>
              <Input
                type="email"
                value={f.email}
                readOnly={emailVerified}
                onChange={(e) => updateEmail(e.target.value)}
                onBlur={() => setEmailTouched(true)}
                className={emailVerified ? "!border-emerald-500" : ""}
              />
              {emailTouched && f.email.trim() && !emailValid ? (
                <p className="text-xs text-brand-bad mt-1">Enter a valid email address.</p>
              ) : null}
              <EmailVerify
                email={f.email}
                purpose="register"
                verified={emailVerified}
                onVerifiedChange={setEmailVerified}
                variant="light"
                onAccountExists={showAccountExistsToast}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-2">Contact Number</label>
              <Input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={f.phone}
                onChange={(e) => setF((p) => ({ ...p, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                onBlur={() => setPhoneTouched(true)}
                placeholder="10-digit mobile number"
              />
              {phoneTouched && f.phone.trim() && !phoneValid ? (
                <p className="text-xs text-brand-bad mt-1">Enter a valid 10-digit mobile number.</p>
              ) : null}
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-2">Password</label>
              <PasswordField
                value={f.password}
                onChange={(e) => setF((p) => ({ ...p, password: e.target.value }))}
                placeholder="Create a strong password"
                autoComplete="new-password"
              />
              {showPasswordChecklist ? (
                <ul className="mt-2 space-y-1 text-xs" aria-live="polite">
                  {PASSWORD_RULES.map((rule) => {
                    const met = rule.test(f.password);
                    return (
                      <li
                        key={rule.label}
                        className={met ? "text-emerald-600 font-medium" : "text-ink-400"}
                      >
                        <span aria-hidden="true">{met ? "✓" : "✕"}</span> {rule.label}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>

            <Button type="submit" variant="primary" fullWidth disabled={!canSubmit}>
              Register →
            </Button>
            {!emailVerified && (
              <p className="text-xs text-ink-400 text-center">Verify your email to enable registration.</p>
            )}
          </form>
          )}
        </div>
      </div>
      <img
        src="/great-place-to-work.png"
        alt="Indira IVF — Great Place To Work Certified, Jan 2026–Jan 2027, India"
        className="hidden sm:block fixed fixed-safe-top-right w-16 md:w-20 h-auto z-10"
        loading="lazy"
      />
    </div>
  );
}
